import {
  TargetDocumentSync,
  TargetItem,
  ScheduledStrictnessRule,
  TargetDocumentSummary,
} from './target-types';
import {
  compareVectorClocks,
  mergeVectorClocks,
  incrementVectorClock,
} from './version-vector';

/**
 * Recalculates document summary metrics from target list.
 */
export function calculateDocumentSummary(targets: TargetItem[]): TargetDocumentSummary {
  const domainCounts: Record<string, number> = {};
  let activeTargets = 0;

  for (const t of targets) {
    domainCounts[t.category] = (domainCounts[t.category] || 0) + 1;
    if (t.enabled) {
      activeTargets++;
    }
  }

  return {
    totalTargets: targets.length,
    activeTargets,
    domainCounts,
  };
}

/**
 * Reconciles two sets of TargetItems using Last-Write-Wins (LWW) field-level resolution.
 * - Targets present on only one side are retained (unioned).
 * - Targets present on both sides are resolved by updatedAt (epoch ms).
 * - Equal timestamps use deterministic tiebreaker (higher hash/lexicographical order).
 */
export function reconcileTargetsLWW(
  localTargets: TargetItem[] = [],
  remoteTargets: TargetItem[] = []
): TargetItem[] {
  const mergedMap = new Map<string, TargetItem>();

  for (const local of localTargets) {
    mergedMap.set(local.id, { ...local });
  }

  for (const remote of remoteTargets) {
    const existing = mergedMap.get(remote.id);
    if (!existing) {
      mergedMap.set(remote.id, { ...remote });
    } else {
      const localUpdated = existing.updatedAt || 0;
      const remoteUpdated = remote.updatedAt || 0;

      if (remoteUpdated > localUpdated) {
        mergedMap.set(remote.id, { ...remote });
      } else if (localUpdated > remoteUpdated) {
        // Local wins, keep existing
      } else {
        // Deterministic tiebreaker: if timestamps are identical, guardian lock wins, then enabled state
        if (remote.isLockedByGuardian && !existing.isLockedByGuardian) {
          mergedMap.set(remote.id, { ...remote });
        } else if (!remote.isLockedByGuardian && existing.isLockedByGuardian) {
          // keep existing
        } else {
          // If all else equal, pick the one with lexicographically larger JSON representation
          const localStr = JSON.stringify(existing);
          const remoteStr = JSON.stringify(remote);
          if (remoteStr >= localStr) {
            mergedMap.set(remote.id, { ...remote });
          }
        }
      }
    }
  }

  return Array.from(mergedMap.values()).sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * Reconciles scheduled strictness rules across categories.
 */
export function reconcileScheduledRules(
  localRules: ScheduledStrictnessRule[] = [],
  remoteRules: ScheduledStrictnessRule[] = [],
  remoteDocTime: number = 0,
  localDocTime: number = 0
): ScheduledStrictnessRule[] {
  const ruleMap = new Map<string, ScheduledStrictnessRule>();

  for (const r of localRules) {
    ruleMap.set(r.categoryId, {
      ...r,
      inactivityTimeoutMinutes: r.inactivityTimeoutMinutes ?? 15,
      continuousAllowanceMinutes: r.continuousAllowanceMinutes ?? 20,
    });
  }

  for (const r of remoteRules) {
    const existing = ruleMap.get(r.categoryId);
    if (!existing) {
      ruleMap.set(r.categoryId, {
        ...r,
        inactivityTimeoutMinutes: r.inactivityTimeoutMinutes ?? 15,
        continuousAllowanceMinutes: r.continuousAllowanceMinutes ?? 20,
      });
    } else {
      // Curfew lock takes precedence
      if (r.isCurfewLocked && !existing.isCurfewLocked) {
        ruleMap.set(r.categoryId, {
          ...r,
          inactivityTimeoutMinutes: r.inactivityTimeoutMinutes ?? existing.inactivityTimeoutMinutes ?? 15,
          continuousAllowanceMinutes: r.continuousAllowanceMinutes ?? existing.continuousAllowanceMinutes ?? 20,
        });
      } else if (!r.isCurfewLocked && existing.isCurfewLocked) {
        // keep local curfew lock
      } else if (remoteDocTime >= localDocTime) {
        ruleMap.set(r.categoryId, {
          ...r,
          inactivityTimeoutMinutes: r.inactivityTimeoutMinutes ?? existing.inactivityTimeoutMinutes ?? 15,
          continuousAllowanceMinutes: r.continuousAllowanceMinutes ?? existing.continuousAllowanceMinutes ?? 20,
        });
      }
    }
  }

  return Array.from(ruleMap.values());
}

/**
 * Full TargetDocumentSync reconciler.
 * Inspects vector clocks, detects dominance vs concurrency, and applies LWW resolution.
 */
export function reconcileTargetDocuments(
  localDoc: TargetDocumentSync,
  remoteDoc: TargetDocumentSync,
  currentDeviceId: string
): {
  document: TargetDocumentSync;
  resolutionType: 'LOCAL_USED' | 'REMOTE_USED' | 'CONCURRENT_MERGED' | 'IDENTICAL';
} {
  const comparison = compareVectorClocks(localDoc.vectorClock, remoteDoc.vectorClock);

  if (comparison.status === 'LOCAL_DOMINATES') {
    return {
      document: localDoc,
      resolutionType: 'LOCAL_USED',
    };
  }

  if (comparison.status === 'REMOTE_DOMINATES') {
    return {
      document: remoteDoc,
      resolutionType: 'REMOTE_USED',
    };
  }

  if (comparison.status === 'IDENTICAL') {
    return {
      document: localDoc,
      resolutionType: 'IDENTICAL',
    };
  }

  // Concurrent Conflict Detected: Perform LWW Merge
  const localTime = Date.parse(localDoc.updatedAt) || 0;
  const remoteTime = Date.parse(remoteDoc.updatedAt) || 0;

  const mergedTargets = reconcileTargetsLWW(localDoc.targets, remoteDoc.targets);
  const mergedRules = reconcileScheduledRules(
    localDoc.scheduledRules,
    remoteDoc.scheduledRules,
    remoteTime,
    localTime
  );

  let mergedClock = mergeVectorClocks(localDoc.vectorClock, remoteDoc.vectorClock);
  mergedClock = incrementVectorClock(mergedClock, currentDeviceId);

  const nextVersion = Math.max(localDoc.version || 0, remoteDoc.version || 0) + 1;
  const summary = calculateDocumentSummary(mergedTargets);

  const mergedDoc: TargetDocumentSync = {
    schemaVersion: 2,
    userId: localDoc.userId || remoteDoc.userId,
    version: nextVersion,
    vectorClock: mergedClock,
    updatedAt: new Date().toISOString(),
    lastModifiedBy: remoteTime > localTime ? remoteDoc.lastModifiedBy : localDoc.lastModifiedBy,
    deviceId: currentDeviceId,
    summary,
    targets: mergedTargets,
    scheduledRules: mergedRules,
    encryptedCustomTargets:
      remoteTime > localTime
        ? remoteDoc.encryptedCustomTargets || localDoc.encryptedCustomTargets
        : localDoc.encryptedCustomTargets || remoteDoc.encryptedCustomTargets,
  };

  return {
    document: mergedDoc,
    resolutionType: 'CONCURRENT_MERGED',
  };
}
